
import type { Route } from 'vue-router'
import type { GuardResolveFunction } from '../models'
import { defineComponent } from 'vue'

import { mapActions, mapGetters } from 'vuex'

import DistributionChart from '../components/insights/analytics/distribution-chart.component.vue'
import KpisSuccess from '../components/insights/analytics/kpis-success.component.vue'
import Pivots from '../components/insights/analytics/pivots/pivots.component.vue'
import QueriesTable from '../components/insights/analytics/queries/queries-table.component.vue'
import RelatedData from '../components/insights/analytics/queries/related-data.component.vue'
import EpbSearch from '../components/insights/analytics/search.component.vue'
import InsightsNoResults from '../components/insights/insights-no-results.component.vue'
import { UiProgressLinear } from '../components/keen-ui'
import { EpbText } from '../components/shared/epb-text'

import { useKpis } from '../composables'

import { KpiEnum } from '../enums'

import { AnalyticsMixin } from '../mixins/analytics.mixin'

import { ANALYTICS_CONSTS } from '../store/analytics/analytics.consts'
import { CONFIG_CONSTS } from '../store/config/config.consts'
import { CONTEXT_CONSTS } from '../store/context/context.consts'

export default defineComponent({
  name: 'Queries',
  components: {
    EpbText,
    InsightsNoResults,
    KpisSuccess,
    DistributionChart,
    Pivots,
    RelatedData,
    EpbSearch,
    QueriesTable,
    UiProgressLinear,
  },
  mixins: [AnalyticsMixin],
  async beforeRouteLeave(to: Route, from: Route, next: GuardResolveFunction): Promise<void> {
    this.contextWatchDisabled = true
    next()
  },
  setup() {
    const { kpiOptions } = useKpis()

    return { kpiOptions }
  },
  data() {
    return {
      loading: {
        distribution: false,
      },
      messages: this.$epb.messages.insights.analytics,
      totalQueries: undefined,
    }
  },
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      kpis: CONFIG_CONSTS.getters.searchKpis,
    }),
    kpisSuccessTitle(): string {
      if (!this.selectedId) {
        return this.messages.kpisSuccess.overall
      } else {
        return this.$epb.utils.compileMessage(this.messages.kpisSuccess.selectedId, {
          terms: this.selectedId,
        })
      }
    },
    hasNoResults(): boolean {
      return !this.loading.distribution && !this.totalQueries
    },
  },
  methods: {
    ...mapActions(CONTEXT_CONSTS.moduleName, {
      setQuery: CONTEXT_CONSTS.actions.setQuery,
    }),
    ...mapActions(ANALYTICS_CONSTS.moduleName, {
      loadDistData: ANALYTICS_CONSTS.actions.loadDistData,
      loadSessionsData: ANALYTICS_CONSTS.actions.loadSessionsData,
    }),
    async loadData(): Promise<void> {
      this.loading.distribution = true
      try {
        await this.loadDistData(this.selectedId)
        this.loading.distribution = false
        if (!this.selectedId) {
          if (
            this.kpis.includes(KpiEnum.SESSIONS) ||
            this.kpis.includes(KpiEnum.QUERIES_PER_SESSION)
          ) {
            await this.loadSessionsData()
          }
          await this.$refs.queriesTable.loadData({ resetAll: true })
        } else {
          await this.$refs.pivotsComponent.loadData()
          await this.$refs.relatedTable.loadData(true)
        }
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      } finally {
        this.loading.distribution = false
      }
    },
    async onSelectedIdChange(selectedId?: string): Promise<void> {
      if (selectedId === this.selectedId) {
        selectedId = undefined
      }
      await this.setSelectedId(selectedId)
      await this.loadData()
    },
    async onQueryChange(query: string, cleanQuery = false): Promise<void> {
      this.contextWatchDisabled = true
      await this.setQuery(query, cleanQuery)
      this.$emit('contextChanged')
      this.contextWatchDisabled = false
    },
  },
})
